import React from "react";
import { useTranslation } from "react-i18next";
import { Package, MessageSquare, Clock, Building2, GripVertical } from "lucide-react";
import { Badge } from "../../../components/ui/Badge";
import { Avatar } from "../../../components/ui/Avatar";
import { RelativeTime } from "../../../components/ui/RelativeTime";
import { getInitials } from "../../../utils/getInitials";
import {
  SEVERITY_TONE,
  typeLabel,
  clientName,
  followingNumber,
  assignee,
  primaryDepartment,
  commentCount,
  ticketAgeMinutes,
} from "./ticketModel";
import { INCIDENT_SEVERITY } from "../../../constants/incidents";

// ageTone: a más viejo el ticket, más urgente se ve (proxy de SLA, no SLA real).
function ageTone(minutes) {
  if (minutes == null) return "text-fg-muted";
  if (minutes > 60 * 48) return "text-status-error";
  if (minutes > 60 * 24) return "text-status-warning";
  return "text-fg-muted";
}

/**
 * TicketCard — tarjeta de ticket para el board y reutilizable.
 * Props:
 *  - incident: documento de incidencia (back)
 *  - onClick: abre el detalle
 *  - draggable / onDragStart / onDragEnd: para el drag entre columnas
 *  - dragging: bool (estado visual mientras se arrastra)
 */
export default function TicketCard({
  incident,
  onClick,
  draggable,
  onDragStart,
  onDragEnd,
  dragging,
}) {
  const { t } = useTranslation();
  const sev = incident.severity;
  const type = typeLabel(incident);
  const client = clientName(incident);
  const follow = followingNumber(incident);
  const person = assignee(incident);
  const dept = primaryDepartment(incident);
  const comments = commentCount(incident);
  const minutes = ticketAgeMinutes(incident);

  return (
    <article
      role="button"
      tabIndex={0}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={[
        "group relative w-full text-left rounded-lg border border-border bg-bg p-3",
        "shadow-card transition-all cursor-pointer",
        "hover:border-brand-300 hover:shadow-elevated focus:outline-none",
        "focus-visible:ring-2 focus-visible:ring-brand-500",
        dragging ? "opacity-50 rotate-1" : "",
      ].join(" ")}
    >
      {/* Fila superior: nº seguimiento + criticidad */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-1 font-mono text-[11px] text-fg-muted">
          <Package className="w-3 h-3 shrink-0" aria-hidden />
          {follow || t("Sin pedido")}
        </span>
        {sev && (
          <Badge tone={SEVERITY_TONE[sev] || "neutral"} dot>
            {t(INCIDENT_SEVERITY[sev] || sev)}
          </Badge>
        )}
      </div>

      {/* Título = tipificación */}
      <h4 className="text-sm font-semibold text-fg leading-snug mb-1.5 line-clamp-2">
        {t(type || "Incidencia sin tipificar")}
      </h4>

      {/* Descripción breve */}
      {incident.description && (
        <p className="text-xs text-fg-muted leading-snug mb-2.5 line-clamp-2">
          {incident.description}
        </p>
      )}

      {/* Tags: área/depto + cliente */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
        <Badge tone="neutral">{t(dept.label)}</Badge>
        {client && (
          <span className="inline-flex items-center gap-1 text-[11px] text-fg-muted max-w-[140px] truncate">
            <Building2 className="w-3 h-3 shrink-0" aria-hidden />
            <span className="truncate">{client}</span>
          </span>
        )}
      </div>

      {/* Pie: responsable + edad + comentarios */}
      <div className="flex items-center justify-between gap-2 pt-2.5 border-t border-border">
        <div className="flex items-center gap-1.5 min-w-0">
          {person ? (
            <>
              <Avatar
                size="xs"
                initials={getInitials(person.name, person.lastName)}
                alt={`${person.name} ${person.lastName || ""}`.trim()}
              />
              <span className="text-[11px] text-fg-muted truncate max-w-[90px]">
                {person.name}
              </span>
            </>
          ) : (
            <span className="text-[11px] text-fg-subtle italic">{t("Sin asignar")}</span>
          )}
        </div>
        <div className="flex items-center gap-2.5 shrink-0 text-[11px]">
          {minutes != null && (
            <span className={`inline-flex items-center gap-1 ${ageTone(minutes)}`}>
              <Clock className="w-3 h-3" aria-hidden />
              <RelativeTime value={incident.createdAt} t={t} />
            </span>
          )}
          {comments > 0 && (
            <span className="inline-flex items-center gap-1 text-fg-muted">
              <MessageSquare className="w-3 h-3" aria-hidden />
              {comments}
            </span>
          )}
        </div>
      </div>

      {/* Manija de drag (solo desktop, aparece al hover) */}
      {draggable && (
        <span
          className="absolute top-2 right-2 text-fg-subtle opacity-0 group-hover:opacity-60 transition-opacity hidden md:block"
          aria-hidden
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
      )}
    </article>
  );
}
